<template>
  <div class="rating-input-container mb-3">
    <label>
      <question-display :question="question"></question-display>
    </label>

    <div v-for="option in ratingOptions" :key="option.value" class="rating-row">
      <!-- attribute name full width on its own line -->
      <label class="star-label">
        {{ option.name }}
        <span v-if="isNumericRating(ratings[option.value])">
          (<small :class="getStatusClass(ratings[option.value])">{{ getStatusLabel(ratings[option.value]) }}</small>)
        </span>
      </label>

      <!-- stars left, Did Not Use button far right — same line -->
      <div class="rating-controls">
        <div class="star-rating" :class="{ faded: isSpecialValue(ratings[option.value]) }">
          <span
            v-for="star in 5"
            :key="star"
            :data-option="option.value"
            @click="rate(option.value, star)"
            :class="['star', { filled: ratings[option.value] === star || ratings[option.value] > star }]"
          >★</span>
        </div>

        <button
          type="button"
          class="special-btn"
          :class="{ active: ratings[option.value] === 'DNU' }"
          @click="setSpecial(option.value, 'DNU')"
        >Don't Know</button>
      </div>
    </div>

    <small
      v-if="showValidation && question.required && hasUnratedOptions"
      class="text-danger"
    >
      All ratings are required.
    </small>
  </div>
</template>

<script>
import surveyService from '@/services/surveyService';
import QuestionDisplay from '../utils/QuestionDisplay.vue';

export default {
  components: { QuestionDisplay },
  props: {
    question: Object,
    required: Boolean,
    showValidation: Boolean
  },
  data() {
    return {
      ratings: surveyService.getAnswer(this.question.id) || this.initializeRatings()
    };
  },
  computed: {
    ratingOptions() {
      return this.question.options.filter(o => o.name.toLowerCase() !== 'did not use');
    },
    hasUnratedOptions() {
      return this.ratingOptions.some(o => this.ratings[o.value] === 0 || this.ratings[o.value] === undefined);
    }
  },
  methods: {
    initializeRatings() {
      const init = {};
      this.question.options.forEach(o => { init[o.value] = 0; });
      return init;
    },
    rate(optionValue, stars) {
      this.ratings = { ...this.ratings, [optionValue]: stars };
      surveyService.setAnswer(this.question.id, this.ratings);
      this.$emit('input', this.ratings);

      const starEls = this.$el.querySelectorAll(`[data-option="${optionValue}"]`);
      starEls.forEach(el => {
        el.classList.remove('animate');
        void el.offsetWidth;
        el.classList.add('animate');
        setTimeout(() => el.classList.remove('animate'), 200);
      });
    },
    setSpecial(optionValue, val) {
      const current = this.ratings[optionValue];
      // Toggle off if already active
      const newVal = current === val ? 0 : val;
      this.ratings = { ...this.ratings, [optionValue]: newVal };
      surveyService.setAnswer(this.question.id, this.ratings);
      this.$emit('input', this.ratings);
    },
    isSpecialValue(val) {
      return val === 'DNU' || val === 'DK';
    },
    isNumericRating(val) {
      return typeof val === 'number' && val > 0;
    },
    getStatusLabel(value) {
      return { 1: 'Poor', 2: 'Below Average', 3: 'Average', 4: 'Good', 5: 'Excellent' }[value] || '';
    },
    getStatusClass(value) {
      return { 1: 'text-danger', 2: 'text-warning', 3: 'text-warning', 4: 'text-primary', 5: 'text-success' }[value] || '';
    }
  },
  watch: {
    question(newQ) {
      this.ratings = surveyService.getAnswer(newQ.id) || this.initializeRatings();
    }
  }
};
</script>

<style scoped>
/* Each attribute block: label full width, then stars+button row below */
.rating-row {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  border-bottom: 1px solid #e8e8e8;
}

.star-label {
  font-weight: 500;
  margin-bottom: 4px;
}

/* stars on left, Did Not Use on far right */
.rating-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.special-btn {
  margin-left: auto;
}

.star-rating {
  display: inline-flex;
  align-items: center;
}

.star-rating.faded .star {
  opacity: 0.35;
}

.star-rating .star {
  font-size: 3rem;
  color: #ccc;
  cursor: pointer;
  transition: transform 0.2s ease, color 0.2s ease;
}

.star-rating .star.filled {
  color: gold;
}

.star-rating .star.animate {
  transform: scale(1.3);
}

.special-btn {
  padding: 4px 14px;
  font-size: 0.82rem;
  border: 1px solid #aaa;
  border-radius: 4px;
  background: #fff;
  color: #555;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s, border-color 0.2s;
}

.special-btn:hover {
  background: #f0f0f0;
}

.special-btn.active {
  background: #4a90d9;
  color: #fff;
  border-color: #3a7bbf;
}
</style>
